import classNames from 'classnames';
import * as qs from 'query-string';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import AppPaths from '../../AppPaths';
import FiMiniImage from '../../assets/images/plp-fi-mini.png';
import FiSeries3PlusImage from '../../assets/images/plp-fi-series-three-plus.png';
import FiUltraImage from '../../assets/images/plp-fi-ultra.png';
import { ReactComponent as ArrowLeft } from '../../assets/images/icons/arrow_left.svg';
import { ReactComponent as ArrowRight } from '../../assets/images/icons/arrow_right.svg';
import { ReactComponent as IconLiveTracking } from '../../assets/images/hardware-first/live-tracking.svg';
import { ultraIsDiscoverable, useLocale } from '../../contexts/LocaleContext';
import { formatHardwarePrice } from '../../lib/hardwareFirst';
import { useIsGrowthBookEnabled } from '../../lib/growthbook';
import useHardwareFirstAddGuard from '../../hooks/useHardwareFirstAddGuard';
import useHardwareFirstShopCardsPricing from '../../hooks/useHardwareFirstShopCardsPricing';
import useShopPromotionOffer from '../../hooks/useShopPromotionOffer';
import { useStickyHeaderOffset } from '../../hooks/useStickyHeaderOffset';
import {
  hardwareFirstProductAdded,
  hardwareFirstProductViewed,
  productListingPage,
  quizQuestionPage,
  ultraPDPShoppingForPickerShown,
  ultraPDPShoppingForSelected,
} from '../../lib/analytics/events';
import { CustomerIO } from '../../lib/analytics/customerIO';
import { useLaunchQuiz } from '../../lib/fiQuiz';
import { useHardwareFirstQuiz } from '../HardwareFirstQuiz/HardwareFirstQuizContext';
import { cartActions } from '../../reducers/storeCart';
import * as types from '../../types';
import ShopProductsJsonLd from '../ShopProductsJsonLd/ShopProductsJsonLd';
import StarRating from '../StarRating/StarRating';
import Button from '../Button';
import { HARDWARE_FIRST_MINI_HERO, hardwareFirstS3Hero } from './hardwareFirstHeroImages';
import { DesktopImageGallery as MiniImageGallery } from '../MiniPDP/MiniImageGallery/MiniImageGallery';
import UltraDesktopImageGallery from '../UltraPDP/UltraDesktopImageGallery/UltraDesktopImageGallery';
import WhoAreYouShoppingFor, { BuyingFor } from '../UltraPDP/WhoAreYouShoppingFor/WhoAreYouShoppingFor';
import useUltraEligiblePets from '../../hooks/useUltraEligiblePets';
import { gallerySlideVideo } from './hardwareFirstVideos';
import {
  DesktopImageGallery as S3ImageGallery,
  layflatImageUrlForVariant,
} from '../../views/ProductDetails/Series3CollarDetails/components/ImageGallery';
import { Image as FiImage } from '../Image';
import { Species } from '../../types/gql-op-types';
import HardwareFirstLearnMoreModal from './HardwareFirstLearnMoreModal';
import HardwareFirstPlanModal from '../HardwareFirstPlanModal/HardwareFirstPlanModal';
import HardwareFirstVariantStep from './HardwareFirstVariantStep';
import useVariantSelector from '../../views/ProductDetails/hooks/useVariantSelector';
import { DeviceQuizAutoOpen } from '../../views/Shop/DeviceQuizRoute';
import { QUIZ_DEVICE_KEYS } from '../HardwareFirstQuiz/rules';
import HardwareFirstBelowFold, { PickYourFiDeviceKey, ValuePropBoxes, WhatsInTheBox } from './HardwareFirstBelowFold';
import {
  defaultShopLineup,
  forgetShopLineup,
  productsFromQuery,
  rememberedShopLineup,
  rememberShopLineup,
} from './hardwareFirstShopQuery';
import styles from './HardwareFirstPickYourFi.module.scss';

type DeviceKey = PickYourFiDeviceKey;

// marketing links land on a specific device (/shop?selected=ultra). aliases cover the
// product ids and spellings an ad build is likely to use alongside the short keys.
const DEVICE_QUERY_ALIASES: Record<string, DeviceKey> = {
  mini: 'mini',
  'fi-mini': 'mini',
  s3: 's3',
  's3-plus': 's3',
  series3: 's3',
  'series-3': 's3',
  'series-3-plus': 's3',
  'smart-collar-v3b': 's3',
  ultra: 'ultra',
  'fi-ultra': 'ultra',
};

function deviceFromQuery(search: string): DeviceKey | undefined {
  const requested = qs.parse(search).selected;
  return typeof requested === 'string' ? DEVICE_QUERY_ALIASES[requested.trim().toLowerCase()] : undefined;
}

/**
 * `?type=cat` puts the page in its cats treatment: mini is the only cat tracker, so the device
 * cards collapse to one and the copy, gallery and below-fold sections all switch. The param name
 * matches the one the standalone pdps already read, so a cats link works on either page.
 */
function speciesFromQuery(search: string): Species {
  return qs.parse(search).type === 'cat' ? Species.CAT : Species.DOG;
}

/** Rewrites only `selected` and drops `step`, so a landing url keeps its utm and coupon params. */
function searchWithDevice(search: string, device: DeviceKey): string {
  const { step, ...rest } = qs.parse(search);
  return qs.stringify({ ...rest, selected: device });
}

/**
 * `?step=size` opens the s3+ size and color step directly, for links that have already settled on
 * the device - the cart's cross-sell, which has no room to ask for a band. A bare `?selected=s3`
 * still lands on the device cards, since that is what a marketing link wants.
 */
function variantStepFromQuery(search: string): boolean {
  return qs.parse(search).step === 'size';
}

// mirrors $desktop-breakpoint in HardwareFirstPickYourFi.module.scss, where the layout goes two-column
const DESKTOP_BREAKPOINT_PX = 1024;

/**
 * On mobile the gallery sits above the tracker cards, so a shopper who picks a tracker from partway
 * down the list never sees the photos change under them. Bring them back up to it. Desktop pins the
 * gallery beside the rail, so it is already in view and the scroll would only be a jolt.
 */
function scrollGalleryIntoView() {
  if (window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT_PX}px)`).matches) return;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
}

interface DeviceCard {
  key: DeviceKey;
  name: string;
  image: string;
  thumb: string;
  description: string;
  pdpPath: string;
  /** sku added to the cart (and priced for display); undefined = the device needs its PDP (variant choice). */
  addSku?: string;
  /** sku used only to price the card when addSku is undefined (any variant of the product). */
  pricingSku?: string;
  trackShopNow: () => void;
  trackLearnMore: () => void;
}

/**
 * whether the card quotes the live promotion's price under its own: only while nothing is taken off
 * yet, and only once the code is known to reprice this tracker; a code the server does not honor
 * for it prices at list and stays quiet
 */
function offerPillShown(
  discount: types.HardwareDiscountPreview | undefined,
  offer: types.HardwareDiscountPreview | undefined,
): boolean {
  return (
    !!discount &&
    discount.listPriceInCents <= discount.discountedPriceInCents &&
    !!offer &&
    offer.promoDiscountInCents > 0
  );
}

function CardPrice({
  discount,
  offer,
}: {
  discount: types.HardwareDiscountPreview | undefined;
  /** the same tracker priced with the live promotion's code, while the shopper has not applied it */
  offer?: types.HardwareDiscountPreview;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  if (!discount) return null;
  const savedInCents = discount.listPriceInCents - discount.discountedPriceInCents;
  const showOffer = offerPillShown(discount, offer) && !!offer;
  return (
    <div className={styles.cardPriceBlock}>
      <div className={styles.cardPrice}>
        <span className={styles.discountedPrice}>{formatHardwarePrice(discount.discountedPriceInCents, locale)}</span>
        {savedInCents > 0 && (
          <span className={styles.listPrice}>{formatHardwarePrice(discount.listPriceInCents, locale)}</span>
        )}
      </div>
      {savedInCents > 0 && (
        <span className={styles.savedPill}>
          {t('hardware_first_card_saved', { amount: formatHardwarePrice(savedInCents, locale) })}
        </span>
      )}
      {showOffer && (
        <span className={styles.offerPill}>
          {t('hardware_first_card_price_with_promo', {
            // the pill is a teaser, not the quote: whole dollars, the exact price lands once the code is on
            price: locale.wholeNumberCurrencyFormatter.format(Math.round(offer.discountedPriceInCents / 100)),
          })}
        </span>
      )}
    </div>
  );
}

/**
 * Hardware-first replacement for the product listing page: a single "Pick your Fi" selection
 * surface with server-priced hardware cards and a one-time-hardware-price add-to-cart bar.
 * Rendered only for arm-enrolled d2c sessions; control keeps the classic PLP.
 */
export default function HardwareFirstPickYourFi() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const launchQuiz = useLaunchQuiz();
  const { pendingDevice, consumePendingDevice } = useHardwareFirstQuiz();
  const locale = useLocale();
  const { country, currency } = locale;
  const { atCap, routeToBag } = useHardwareFirstAddGuard();
  const products = useSelector((state: types.AppState) => state.config.products);
  const internalTester = useSelector((state: types.AppState) => !!state.config.siteConfig.internalTester);
  // ultra is country- and policy-gated exactly as it is on the classic plp; without this
  // an unsupported market could add an lte/satellite device straight to the bag
  const isUltraAvailable = useIsGrowthBookEnabled() && ultraIsDiscoverable(country, internalTester);
  const loggedIn = useSelector((state: types.AppState) => !!state.session);
  const { pets: eligiblePets } = useUltraEligiblePets();
  const species = speciesFromQuery(location.search);
  const isCats = species === Species.CAT;
  // a landing link's lineup outlives its url: the bag's back button and the nav come back to bare
  // /shop and the shopper should see the same trackers. an explicit `selected=` outside the
  // remembered lineup is the shopper stepping out of it (a nav tap on a hidden tracker), so it wins.
  // with neither, the shop opens without ultra unless the link selected it.
  const urlProducts = productsFromQuery(location.search);
  const remembered = rememberedShopLineup();
  const urlSelected = deviceFromQuery(location.search);
  const leavesLineup = !urlProducts && !!remembered && !!urlSelected && !remembered.includes(urlSelected);
  const requestedProducts = urlProducts ?? (leavesLineup ? undefined : (remembered ?? defaultShopLineup(urlSelected)));
  useEffect(() => {
    if (leavesLineup) {
      forgetShopLineup();
    } else {
      rememberShopLineup(location.search);
    }
  }, [location.search, leavesLineup]);
  // mini is preselected so the footer price and below-fold have content on first paint; a
  // ?selected= link overrides it once, on mount, after which the shopper's taps own the choice
  const [selectedKey, setSelectedKey] = useState<DeviceKey>(() => deviceFromQuery(location.search) ?? 'mini');

  // mirror the choice into the url so a refresh or a shared link keeps it. `replace`, not
  // `push`: pushing would turn the back button into a device-selection rewind.
  const selectDevice = useCallback(
    (device: DeviceKey) => {
      setSelectedKey(device);
      history.replace({ search: searchWithDevice(location.search, device) });
    },
    [history, location.search],
  );

  // a tap on a card is the one path where the shopper is looking at the list rather than the
  // gallery; the quiz and the url set the device without moving the page
  const onCardTap = (device: DeviceKey) => {
    if (device !== selectedKey) scrollGalleryIntoView();
    selectDevice(device);
  };

  // the quiz sheet is mounted app-wide, so it hands its recommendation back
  // rather than reaching into this page's selection directly
  useEffect(() => {
    if (pendingDevice) {
      selectDevice(pendingDevice);
      consumePendingDevice();
    }
  }, [pendingDevice, selectDevice, consumePendingDevice]);
  // s3+ needs a band size and color before it can be added, so its cta opens a
  // variant step rather than going straight to the bag
  const [variantStepOpen, setVariantStepOpen] = useState(
    () => variantStepFromQuery(location.search) && deviceFromQuery(location.search) === 's3',
  );

  /** Returns to the device cards and drops `step`, so a refresh does not reopen the step. */
  const closeVariantStep = () => {
    setVariantStepOpen(false);
    history.replace({ search: searchWithDevice(location.search, 's3') });
  };
  const miniProduct = products.find((p) => p.id === types.miniCollarId) as types.IMiniProduct | undefined;
  const s3Product = products.find((p) => p.id === types.series3PlusCollarId) as types.ICollarKitProduct | undefined;
  const ultraProduct = products.find((p) => p.id === types.ultraCollarId) as types.IUltraProduct | undefined;

  const devices: DeviceCard[] = [
    {
      key: 'mini',
      name: 'Fi Mini',
      image: FiMiniImage,
      thumb: '/product_images/mini/device.png',
      description: t('hardware_first_description_mini'),
      pdpPath: AppPaths.Products('fi-mini') + '?type=dog',
      addSku: miniProduct?.sku,
      trackShopNow: productListingPage.miniShopNow,
      trackLearnMore: productListingPage.miniLearnMore,
    },
    {
      key: 's3',
      name: 'Fi Series 3+',
      image: FiSeries3PlusImage,
      thumb: '/product_images/smart-collar-v3b/gray.png',
      description: t('hardware_first_description_s3'),
      pdpPath: AppPaths.Products(types.series3PlusCollarId),
      // band size/color live on the PDP, so adding routes there instead of straight to the bag
      pricingSku: s3Product?.variants[0]?.sku,
      trackShopNow: productListingPage.series3PlusShopNow,
      trackLearnMore: productListingPage.series3PlusLearnMore,
    },
    {
      key: 'ultra',
      name: 'Fi Ultra',
      image: FiUltraImage,
      thumb: '/product_images/smart-collar-ultra.png',
      description: t('hardware_first_description_ultra'),
      pdpPath: AppPaths.Products('fi-ultra'),
      addSku: ultraProduct?.sku,
      trackShopNow: productListingPage.ultraShopNow,
      trackLearnMore: productListingPage.ultraLearnMore,
    },
  ];

  // the cards quote the applied code's price; a promotion the shopper has not applied quotes list,
  // and while that promotion's code is not on the cart each card also quotes its price under it
  const offerCode = useShopPromotionOffer();
  const skusByKey: Record<DeviceKey, string | undefined> = {
    mini: devices[0].addSku,
    s3: devices[1].pricingSku,
    ultra: isUltraAvailable ? devices[2].addSku : undefined,
  };
  const { discounts, offers } = useHardwareFirstShopCardsPricing(Object.values(skusByKey), offerCode);
  const pricedFor = (bySku: typeof discounts, key: DeviceKey) => {
    const sku = skusByKey[key];
    return sku ? bySku[sku] : undefined;
  };
  const discountsByKey: Record<DeviceKey, types.HardwareDiscountPreview | undefined> = {
    mini: pricedFor(discounts, 'mini'),
    s3: pricedFor(discounts, 's3'),
    ultra: pricedFor(discounts, 'ultra'),
  };
  const offersByKey: Record<DeviceKey, types.HardwareDiscountPreview | undefined> = {
    mini: pricedFor(offers, 'mini'),
    s3: pricedFor(offers, 's3'),
    ultra: pricedFor(offers, 'ultra'),
  };

  const s3Variants = s3Product?.variants ?? [];
  const noop = () => undefined;
  const { changeVariantOptions, selectedVariant, hasUpdatedSize } = useVariantSelector({
    onColorChange: noop,
    onSelectedVariantChange: noop,
    onSizeChange: noop,
    variants: s3Variants,
    useQueryParams: false,
  });

  const gatedDevices = isCats
    ? devices.filter((device) => device.key === 'mini')
    : devices.filter((device) => device.key !== 'ultra' || isUltraAvailable);
  // a `products=` link narrows the lineup to the trackers it names. one that names nothing this
  // market can show falls back to the full lineup rather than an empty page.
  const requestedDevices = requestedProducts
    ? gatedDevices.filter((device) => requestedProducts.includes(device.key))
    : gatedDevices;
  const visibleDevices = requestedDevices.length > 0 ? requestedDevices : gatedDevices;
  // a narrowed page loses the "three trackers" subtitle: the link already chose
  const lineupNarrowed = visibleDevices.length < gatedDevices.length;
  // the quiz picks between mini and s3+, so a narrowed page keeps it only while both are on it. a
  // lineup missing either would let the quiz recommend a tracker the page is hiding.
  const quizAvailable =
    !lineupNarrowed || QUIZ_DEVICE_KEYS.every((key) => visibleDevices.some((device) => device.key === key));
  const selected = visibleDevices.find((d) => d.key === selectedKey) ?? visibleDevices[0];

  // a signed-in member with a dog on an eligible membership can add an ultra to it instead of
  // starting a new one. the choice rides the cart item so the order records which purchase it was;
  // the price is the same either way. everyone else buys standalone without being asked.
  const showUltraPicker = selected.key === 'ultra' && loggedIn && eligiblePets.length > 0;
  const [buyingFor, setBuyingFor] = useState<BuyingFor>('existing');
  const ultraAttachment = showUltraPicker && buyingFor === 'existing';
  useEffect(() => {
    if (showUltraPicker) {
      ultraPDPShoppingForPickerShown({ eligiblePetCount: eligiblePets.length });
    }
  }, [showUltraPicker, eligiblePets.length]);
  const onSelectBuyingFor = (choice: BuyingFor) => {
    setBuyingFor(choice);
    ultraPDPShoppingForSelected({ choice, eligiblePetCount: eligiblePets.length });
  };
  const selectedDiscount = discountsByKey[selected.key];

  // the selected card is the product page here, so it reports a view with the price it shows.
  // fires once the preview lands (the card has no price row before that) and again when a code
  // reprices it, matching the pdps, which re-fire when the quoted bundle changes.
  const pricedSku = selected.addSku ?? selected.pricingSku;
  useEffect(() => {
    if (pricedSku && selectedDiscount) {
      hardwareFirstProductViewed({ sku: pricedSku, currency, discount: selectedDiscount });
    }
  }, [pricedSku, selectedDiscount, currency]);

  // mini and ultra add straight to the bag; s3+ asks for a band size and color first, so its
  // cta names that step until the shopper is in it
  const s3StepNeedsSize = selected.key === 's3' && variantStepOpen && !hasUpdatedSize;
  const ctaLabel =
    selected.key === 's3' && !variantStepOpen ? t('hardware_first_select_size_color') : t('hardware_first_add_to_cart');
  const showVariantStep = variantStepOpen && selected.key === 's3' && !!selectedVariant;

  // the selected card's cta is desktop-only, so the pinned bar gives way to it only there, while
  // it is in view. on mobile, and in the variant step, the bar is the only cta and stays up.
  // the hero column stays put while the rail scrolls, so the tracker is in front of the shopper
  // for the whole decision. the app bar is sticky and its height changes with the promotion
  // banner and the scroll collapse, so the offset is measured rather than hardcoded.
  const heroRef = useRef<HTMLDivElement>(null);
  useStickyHeaderOffset(heroRef, '--hf-sticky-top');

  const cardCtaRef = useRef<HTMLDivElement>(null);
  const [cardCtaVisible, setCardCtaVisible] = useState(true);
  useEffect(() => {
    const target = cardCtaRef.current;
    if (!target || typeof IntersectionObserver === 'undefined') {
      setCardCtaVisible(false);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setCardCtaVisible(entry.isIntersecting));
    observer.observe(target);
    return () => observer.disconnect();
  }, [selected.key, showVariantStep]);
  const footerHidden = !showVariantStep && cardCtaVisible;

  // the step swaps the whole rail in place, so the page jumps to the top before paint;
  // otherwise the pickers land wherever the tracker list was scrolled to and the color row
  // sits above the fold. the app-level scroll reset only fires on a pathname change.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [showVariantStep]);

  const onQuiz = () => {
    quizQuestionPage.quizButtonTapped();
    launchQuiz();
  };

  const onAddToCart = () => {
    selected.trackShopNow();
    // s3+ collects size and color first; the variant step's own cta adds to the bag
    if (selected.key === 's3' && !variantStepOpen) {
      setVariantStepOpen(true);
      return;
    }
    const sku = selected.key === 's3' ? selectedVariant?.sku : selected.addSku;
    if (!sku) {
      history.push(selected.pdpPath);
      return;
    }
    // at the unit cap, skip the add the server would reject; the bag's bulk-sales notice explains
    if (!atCap) {
      dispatch(
        cartActions.addSingleLineCartItem({ sku, currency, ...(selected.key === 'ultra' && { ultraAttachment }) }),
      );
      hardwareFirstProductAdded({ sku, currency, source: 'hardware_first_plp', discount: selectedDiscount });
      CustomerIO.trackAddedItemToCart(
        selected.key === 'mini'
          ? types.miniCollarId
          : selected.key === 's3'
            ? types.series3PlusCollarId
            : types.ultraCollarId,
      );
    }
    routeToBag();
  };

  // the video rides along as the last gallery slide; cats have no footage of their own
  const gallerySlides = useMemo(
    () => (species === Species.CAT ? undefined : [gallerySlideVideo(selected.key)]),
    [species, selected.key],
  );

  return (
    <div className={`${styles.pageBackground} hardware-first-plp`}>
      <div className={styles.page}>
        <div className={classNames(styles.layout, { [styles.layoutStepped]: showVariantStep })}>
          <div className={styles.hero} ref={heroRef}>
            {showVariantStep ? (
              <>
                <div className={styles.variantHeader}>
                  {/* a dismiss control reads as an alert; a back link says the shopper is inside a
                      flow and can step out of it */}
                  <button className={styles.variantBack} onClick={closeVariantStep} type="button">
                    <ArrowLeft />
                    <span>{t('hardware_first_back_to_trackers')}</span>
                  </button>
                </div>
                <div className={styles.variantHero}>
                  <FiImage
                    image={
                      hardwareFirstS3Hero(selectedVariant) ?? {
                        type: 'image',
                        url: layflatImageUrlForVariant(selectedVariant),
                        descriptors: ['1500w', '3000w'],
                      }
                    }
                    alt={selected.name}
                  />
                </div>
              </>
            ) : (
              <div className={styles.gallery}>
                {selected.key === 'mini' && (
                  <MiniImageGallery
                    heroImage={HARDWARE_FIRST_MINI_HERO}
                    species={species}
                    prevNextButtons
                    appendMedia={gallerySlides}
                    animateThumbnailSelect
                  />
                )}
                {selected.key === 's3' && (
                  <S3ImageGallery
                    heroImage={hardwareFirstS3Hero(s3Product?.variants[0])}
                    selectedVariant={s3Product?.variants[0]}
                    prevNextButtons
                    appendMedia={gallerySlides}
                    animateThumbnailSelect
                  />
                )}
                {selected.key === 'ultra' && (
                  <UltraDesktopImageGallery prevNextButtons appendMedia={gallerySlides} animateThumbnailSelect />
                )}
              </div>
            )}
          </div>
          <div className={styles.rail}>
            {/* the mobile size and color step opens straight on the collar and the pickers */}
            <div className={classNames(styles.headerCopy, { [styles.headerCopyStepped]: showVariantStep })}>
              {/* the heading names the tracker in the gallery, so on mobile, where the cards sit below
                  it, the shopper reads which one they are looking at without scrolling back down */}
              <h1 className={styles.title}>{isCats ? t('hardware_first_mini_for_cats') : selected.name}</h1>
              {!lineupNarrowed && (
                <div className={styles.subtitle}>
                  {t(isCats ? 'hardware_first_mini_for_cats_subtitle' : 'hardware_first_pick_your_fi_subtitle')}
                </div>
              )}
              <div className={styles.starRow}>
                <StarRating />
              </div>
              <ShopProductsJsonLd
                products={devices.map(({ key, name, thumb, description, addSku, pricingSku }) => {
                  // listings use the product line's own one-liners: the mini card on the cats page is
                  // "Fi Mini for cats", and ultra's listing copy is not its card's off-leash pitch
                  const isCatMini = isCats && key === 'mini';
                  const listingDescription = isCatMini
                    ? t('product_description_mini_cats')
                    : key === 'ultra'
                      ? t('product_description_ultra')
                      : description;
                  return {
                    key,
                    name: isCatMini ? t('hardware_first_mini_for_cats') : name,
                    thumb,
                    description: listingDescription,
                    sku: addSku ?? pricingSku,
                  };
                })}
                discountsByKey={discountsByKey}
              />
              {/* the quiz compares mini and s3+; a lineup without both has already decided */}
              {quizAvailable && (
                <button className={styles.quizRow} onClick={onQuiz} type="button">
                  <span className={styles.quizText}>
                    <span className={styles.quizPrompt}>{t('hardware_first_quiz_prompt')}</span>
                    <span className={styles.quizCta}>{t('hardware_first_quiz_cta')}</span>
                  </span>
                  <ArrowRight />
                </button>
              )}
            </div>
            {showUltraPicker && (
              <WhoAreYouShoppingFor
                petNames={eligiblePets.map((pet) => pet.name)}
                selected={buyingFor}
                onSelect={onSelectBuyingFor}
              />
            )}
            {showVariantStep ? (
              <div className={styles.variantStep}>
                <HardwareFirstVariantStep
                  changeVariantOptions={changeVariantOptions}
                  hasUpdatedSize={hasUpdatedSize}
                  selectedVariant={selectedVariant}
                  variants={s3Variants}
                />
              </div>
            ) : (
              visibleDevices.map((device) => (
                <div
                  key={device.key}
                  className={classNames(styles.card, { [styles.cardSelected]: device.key === selected.key })}
                  onClick={() => onCardTap(device.key)}
                >
                  <div
                    className={classNames(styles.cardHeader, {
                      [styles.cardHeaderOffer]: offerPillShown(discountsByKey[device.key], offersByKey[device.key]),
                    })}
                  >
                    <img className={styles.cardThumb} src={device.thumb} alt={device.name} />
                    <div className={styles.cardName}>{device.name}</div>
                    <CardPrice discount={discountsByKey[device.key]} offer={offersByKey[device.key]} />
                  </div>
                  <div className={styles.cardDivider} />
                  <div className={styles.cardDescription}>{device.description}</div>
                  <HardwareFirstLearnMoreModal
                    deviceKey={device.key}
                    species={species}
                    name={device.name}
                    discount={discountsByKey[device.key]}
                    onOpen={device.trackLearnMore}
                    onSelect={() => {
                      selectDevice(device.key);
                      if (device.key === 's3') {
                        setVariantStepOpen(true);
                      }
                    }}
                    trigger={
                      <button className={styles.learnMore} type="button">
                        {t('learn_more')}
                      </button>
                    }
                  />
                  {device.key === selected.key && (
                    // stops the card's select handler, which would rewrite the url after the add routes away
                    <div className={styles.cardCta} onClick={(event) => event.stopPropagation()} ref={cardCtaRef}>
                      <Button className={styles.cardCtaButton} onClick={onAddToCart}>
                        {ctaLabel}
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
            {!showVariantStep && (
              <>
                {!ultraAttachment && (
                  <div className={styles.planCard}>
                    <div className={styles.planCardRow}>
                      <IconLiveTracking className={styles.planCardIcon} />
                      <div className={styles.planCardTitle}>{t('hardware_first_plan_card_title')}</div>
                    </div>
                    <div className={styles.planCardBody}>{t('hardware_first_plan_required_at_activation')}</div>
                    <HardwareFirstPlanModal
                      trigger={
                        <button className={styles.planCardCta} type="button">
                          {t('hardware_first_plan_card_cta')}
                        </button>
                      }
                    />
                  </div>
                )}
                <ValuePropBoxes />
                <WhatsInTheBox deviceKey={selected.key} />
              </>
            )}
          </div>
        </div>
        {/* the size and color step is a decision surface: only the collar and the pickers */}
        <DeviceQuizAutoOpen available={quizAvailable} />
        {!showVariantStep && (
          <HardwareFirstBelowFold deviceKey={selected.key} species={species} showQuiz={quizAvailable} />
        )}
        <div className={classNames(styles.footer, { [styles.footerHidden]: footerHidden })}>
          <div className={styles.footerInner}>
            {selectedDiscount && (
              <div className={styles.footerPriceRow}>
                <span className={styles.footerLabel}>{selected.name}</span>
                <span className={styles.footerPrice}>
                  {selectedDiscount.listPriceInCents > selectedDiscount.discountedPriceInCents && (
                    <span className={styles.offPill}>
                      {selectedDiscount.promoDiscountInCents > 0
                        ? t('hardware_first_percent_off', {
                            percent: Math.round(
                              (selectedDiscount.promoDiscountInCents / selectedDiscount.listPriceInCents) * 100,
                            ),
                          })
                        : t('hardware_first_amount_off', {
                            amount: formatHardwarePrice(
                              selectedDiscount.listPriceInCents - selectedDiscount.discountedPriceInCents,
                              locale,
                            ),
                          })}
                    </span>
                  )}
                  <span className={styles.discountedPrice}>
                    {formatHardwarePrice(selectedDiscount.discountedPriceInCents, locale)}
                  </span>
                  {selectedDiscount.listPriceInCents > selectedDiscount.discountedPriceInCents && (
                    <span className={styles.listPrice}>
                      {formatHardwarePrice(selectedDiscount.listPriceInCents, locale)}
                    </span>
                  )}
                </span>
              </div>
            )}
            <Button className={styles.addToCart} disabled={s3StepNeedsSize} onClick={onAddToCart}>
              {ctaLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
